(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const initializeHeroVideo = () => {
    const video = document.querySelector('.fiole-home__hero-video');
    if (!video) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const savesData = Boolean(navigator.connection?.saveData);
    video.muted = true;
    video.playsInline = true;

    if (prefersReducedMotion || savesData) {
      video.pause();
      video.removeAttribute('autoplay');
      return;
    }

    const setPlayback = (visible) => {
      if (document.hidden || !visible) {
        video.pause();
        return;
      }
      const playRequest = video.play();
      if (playRequest?.catch) playRequest.catch(() => {});
    };

    let isVisible = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        setPlayback(isVisible);
      },
      { threshold: 0.2 }
    );
    observer.observe(video);
    document.addEventListener('visibilitychange', () => setPlayback(isVisible));
  };

  const initializeProductConversion = () => {
    const product = document.querySelector('product-info');
    const info = product?.querySelector('.product__info-container');
    if (!product || !info || document.querySelector('.fiole-sticky-buy')) return;

    const isFrench = (document.documentElement.lang || '').toLowerCase().startsWith('fr');
    const quickBuyLabel = isFrench ? 'Achat rapide' : 'Quick buy';
    const selectionFallback = isFrench ? 'Votre sélection' : 'Your selection';

    const getForm = () => product.querySelector('form[data-type="add-to-cart-form"]') || product.querySelector('product-form form[action*="/cart/add"]');
    const getSubmit = () => getForm()?.querySelector('button[name="add"]');
    const getSubmitLabel = () => getSubmit()?.querySelector('span');

    const ensureAssurance = () => {
      if (info.querySelector('.fiole-product-assurance')) return;
      const items = isFrench
        ? ['Créé à Montréal', 'Paiement sécurisé', 'Rechargeable', 'Assistance directe']
        : ['Created in Montréal', 'Secure checkout', 'Refillable', 'Direct support'];
      const box = document.createElement('div');
      box.className = 'fiole-product-assurance';
      box.setAttribute('aria-label', isFrench ? 'Avantages du produit' : 'Product benefits');
      items.forEach((item) => {
        const benefit = document.createElement('span');
        benefit.textContent = item;
        box.appendChild(benefit);
      });
      const buttons = info.querySelector('.product-form__buttons');
      (buttons || info).insertAdjacentElement('afterend', box);
    };

    const stickyBar = document.createElement('aside');
    stickyBar.className = 'fiole-sticky-buy';
    stickyBar.setAttribute('aria-label', quickBuyLabel);
    stickyBar.innerHTML = `
      <div class="fiole-sticky-buy__meta">
        <span class="fiole-sticky-buy__title"></span>
        <span class="fiole-sticky-buy__selection" hidden></span>
        <span class="fiole-sticky-buy__price"></span>
      </div>
      <button class="fiole-sticky-buy__button" type="button"></button>
    `;
    document.body.appendChild(stickyBar);

    const titleNode = stickyBar.querySelector('.fiole-sticky-buy__title');
    const selectionNode = stickyBar.querySelector('.fiole-sticky-buy__selection');
    const priceNode = stickyBar.querySelector('.fiole-sticky-buy__price');
    const stickyButton = stickyBar.querySelector('.fiole-sticky-buy__button');

    const visiblePrice = () => {
      const prices = Array.from(
        info.querySelectorAll('.price-item--sale:not(s), .price-item--regular:not(s)')
      );
      return prices.find((price) => price.offsetParent !== null)?.textContent.trim() || '';
    };

    const selectedOptions = () => {
      const offer = info.querySelector('[data-fiole-offer]');
      const offerChoice = offer?.querySelector('input[type="radio"]:checked');
      if (offerChoice?.value === 'duo') {
        const first = offer.querySelector('[data-duo-first]')?.value;
        const second = offer.querySelector('[data-duo-second]')?.value;
        return ['Duo', first && second ? `${first} + ${second}` : ''].filter(Boolean).join(' · ');
      }

      const values = [];
      info.querySelectorAll('.product-form__input').forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        if (checked) {
          values.push(checked.value);
        } else if (select?.value) {
          values.push(select.options[select.selectedIndex]?.textContent.trim() || select.value);
        }
      });
      return values.filter(Boolean).join(' · ');
    };

    const setText = (node, value) => {
      if (node && node.textContent !== value) node.textContent = value;
    };

    const synchronize = () => {
      ensureAssurance();
      const submit = getSubmit();
      const submitLabel = getSubmitLabel();
      if (!submit || !submitLabel) return;

      const productTitle = document.querySelector('.product__title h1')?.textContent.trim() || '';
      const price = visiblePrice();
      const selection = selectedOptions();
      const summary = info.querySelector('[data-fiole-selection-summary]');
      const summaryValue = summary?.querySelector('[data-fiole-selection-value]');

      setText(titleNode, productTitle);
      setText(priceNode, price);
      setText(stickyButton, submitLabel.textContent.trim());
      stickyButton.disabled = submit.disabled;

      if (selection) {
        setText(selectionNode, `${selectionFallback}: ${selection}`);
        selectionNode.hidden = false;
        setText(summaryValue, selection);
      } else {
        selectionNode.hidden = true;
      }
    };

    stickyButton.addEventListener('click', () => getSubmit()?.click());
    product.addEventListener('change', () => window.requestAnimationFrame(synchronize));
    document.addEventListener('variant:change', () => window.requestAnimationFrame(synchronize));

    let hasPassedPrimaryButton = false;
    const observePrimaryButton = () => {
      const submit = getSubmit();
      if (!submit || submit.dataset.fioleObserved === 'true') return;
      submit.dataset.fioleObserved = 'true';
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
            return;
          }
          if (entry.boundingClientRect.top < 0) hasPassedPrimaryButton = true;
          stickyBar.classList.toggle('is-visible', hasPassedPrimaryButton);
        },
        { threshold: 0.15 }
      );
      observer.observe(submit);
    };

    const productObserver = new MutationObserver(() => {
      synchronize();
      observePrimaryButton();
    });
    productObserver.observe(info, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });

    ensureAssurance();
    synchronize();
    observePrimaryButton();
  };

  onReady(() => {
    initializeHeroVideo();
    initializeProductConversion();
  });
})();
